import { useState, type FormEvent } from 'react';
import { ChevronRightIcon } from 'lucide-react';

import { Button } from '@/components/atoms';

import '../add-new-language/language.scss';
import './table-size-modal.scss';

const MinSize = 1;
const MaxSize = 100;
const DefaultRows = 3;
const DefaultCols = 3;

type Props = {
  initialRows?: number;
  initialCols?: number;
  title?: string;
  confirmLabel?: string;
  onConfirm: (rows: number, cols: number) => void;
  onCancel: () => void;
};

function clampSize(value: number): number {
  return Math.min(MaxSize, Math.max(MinSize, Math.floor(value)));
}

function TableSizeForm({
  initialRows = DefaultRows,
  initialCols = DefaultCols,
  title = 'Choose the number of rows and columns for your table.',
  confirmLabel = 'Create table',
  onConfirm,
  onCancel,
}: Props) {
  const [rows, setRows] = useState(String(initialRows));
  const [cols, setCols] = useState(String(initialCols));

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    onConfirm(clampSize(Number(rows)), clampSize(Number(cols)));
  }

  return (
    <form className="table-size-modal add-language" onSubmit={handleSubmit}>
      <div className="add-language__content table-size-modal__content">
        <p className="table-size-modal__hint">{title}</p>
        <div className="table-size-modal__fields">
          <label className="table-size-modal__field">
            <span className="table-size-modal__label">Rows</span>
            <input
              type="number"
              min={MinSize}
              max={MaxSize}
              value={rows}
              onChange={e => setRows(e.target.value)}
            />
          </label>
          <label className="table-size-modal__field">
            <span className="table-size-modal__label">Columns</span>
            <input
              type="number"
              min={MinSize}
              max={MaxSize}
              value={cols}
              onChange={e => setCols(e.target.value)}
            />
          </label>
        </div>
      </div>
      <div className="add-language__footer table-size-modal__footer">
        <Button type="button" variant="alert" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">
          {confirmLabel}
          <ChevronRightIcon size={14} />
        </Button>
      </div>
    </form>
  );
}

export { TableSizeForm, DefaultRows, DefaultCols, MaxSize, MinSize };
